import React, { useEffect, useMemo, useState } from "react";
import { db } from "@/config/firebase";
import { collection, getDocs } from "firebase/firestore";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { computeBetPoints } from "@/utils/scoring";

export default function BetsLeaderboard() {
  const { role } = useAuth();
  const [bets, setBets] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);

  const load = async () => {
    const bs = await getDocs(collection(db, "bets"));
    setBets(bs.docs.map((d) => ({ id: d.id, ...d.data() })));
    const ev = await getDocs(collection(db, "events"));
    setEvents(ev.docs.map((d) => ({ id: d.id, ...d.data() })));
    const us = await getDocs(collection(db, "users"));
    setUsers(us.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => { load(); }, []);

  const finalGames = useMemo(() => events.filter((e) => e.type === "game" && e.status === "final"), [events]);
  const usersById = useMemo(() => Object.fromEntries(users.map((u) => [u.auth_uid || u.id, u])), [users]);

  // Leaderboard aggregation
  const leaderboard = useMemo(() => {
    const finalMap = Object.fromEntries(finalGames.map((g) => [g.id, g]));
    const agg = new Map();
    for (const b of bets) {
      const g = finalMap[b.game_id];
      const uid = b.user_id;
      const entry = agg.get(uid) || { points: 0, correctWins: 0, deviations: [], count: 0 };
      let pts = Number(b.points_awarded || 0);
      if (!pts && g) {
        const real = { h: Number(g.final_home_score||0), a: Number(g.final_away_score||0), t: Number(g.final_technicals||0) };
        const pred = { h: Number(b.predicted_home_score||0), a: Number(b.predicted_away_score||0), t: Number(b.predicted_technicals||0) };
        pts = computeBetPoints(pred, real);
      }
      entry.points += pts;
      if (g) {
        const predWinner = b.predicted_home_score === b.predicted_away_score ? 0 : b.predicted_home_score > b.predicted_away_score ? 1 : -1;
        const realWinner = g.final_home_score === g.final_away_score ? 0 : g.final_home_score > g.final_away_score ? 1 : -1;
        if (predWinner === realWinner) entry.correctWins += 1;
        entry.deviations.push(Math.abs(b.predicted_home_score - g.final_home_score) + Math.abs(b.predicted_away_score - g.final_away_score));
      }
      entry.count += 1;
      agg.set(uid, entry);
    }
    const rows = Array.from(agg.entries()).map(([uid, v]) => ({
      uid,
      name: usersById[uid]?.display_name || usersById[uid]?.name || usersById[uid]?.email || uid.slice(0,6)+"…",
      points: v.points,
      correctWins: v.correctWins,
      avgDeviation: v.deviations.length ? (v.deviations.reduce((a,b)=>a+b,0)/v.deviations.length).toFixed(1) : 0,
      count: v.count,
    }));
    rows.sort((a,b)=>b.points - a.points);
    return rows;
  }, [bets, finalGames, usersById]);

  return (
    <div className="pb-28 min-h-screen bg-[#0e0b0c] text-rose-50 px-4 pt-6">
      <h2 className="text-xl font-bold mb-3">Wetten Leaderboard</h2>

      {/* Punktevergabe Erklärung */}
      <Card className="bg-[#1a0d11] border-white/10 mb-4">
        <CardContent className="pt-4">
          <h3 className="font-semibold mb-2 text-sm">📊 Punktevergabe</h3>
          <ul className="text-xs text-rose-200/80 space-y-1">
            <li>• <strong>+5 Punkte:</strong> Sieger korrekt getippt (Heim, Auswärts oder Unentschieden)</li>
            <li>• <strong>+10 Punkte (max):</strong> Für exakte Ergebnisse (10 - Differenz, min 0)</li>
            <li>• <strong>+1 Punkt:</strong> Wenn 0 technische Fouls korrekt getippt</li>
            <li>• <strong>+3 Punkte:</strong> Wenn technische Fouls exakt korrekt</li>
          </ul>
          <div className="text-xs text-rose-200/60 mt-2">
            Beispiel: Tipp 70:65, Ergebnis 72:63 → +5 (Sieger) + 6 (10 - |70-72| - |65-63|) = 11 Punkte
          </div>
        </CardContent>
      </Card>

      {/* Leaderboard */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold">Rangliste (Alle Spieler)</h3>
          {role === "admin" && (
            <Button onClick={()=>{if(confirm("Season zurücksetzen?")) load();}} className="bg-white/10 hover:bg-white/20 text-xs">Season zurücksetzen</Button>
          )}
        </div>
        {leaderboard.length===0 ? (
          <div className="text-sm text-rose-200/80">Noch keine Auswertung.</div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-sm">
              <thead className="bg-[#160b0f] text-rose-200/80">
                <tr>
                  <th className="text-left p-2">Platz</th>
                  <th className="text-left p-2">Name</th>
                  <th className="text-left p-2">Punkte</th>
                  <th className="text-left p-2">Tipps</th>
                  <th className="text-left p-2">Sieger ✓</th>
                  <th className="text-left p-2">Ø Abw</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((row, idx) => (
                  <tr key={row.uid} className="border-t border-white/10 hover:bg-white/5">
                    <td className="p-2 font-mono text-rose-300">{idx+1}</td>
                    <td className="p-2">{row.name}</td>
                    <td className="p-2 font-bold">{row.points}</td>
                    <td className="p-2 text-rose-200/70">{row.count}</td>
                    <td className="p-2 text-green-400">{row.correctWins}</td>
                    <td className="p-2 text-rose-200/70">{row.avgDeviation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
